import { Anchor } from "@mantine/core"
import { IconExternalLink } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  ActionIcon,
  DateCell,
  getCheckboxColumnDefs,
  Missing,
  TextCell,
} from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { LastPendingIrContactCell } from "~/components/cells/LastPendingIrContactCell.tsx"
import { SupplierCell } from "~/components/cells/SupplierCell.tsx"
import { ProductCatalogListItemResponseDto } from "~/gen/api"
import { recycledMaterialPercentageDtoCauseEnum } from "~/gen/api/types/RecycledMaterialPercentageDto"

const columnHelper = createColumnHelper<ProductCatalogListItemResponseDto>()

export const useCatalogColumns = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(["products"])

  return useMemo<ColumnDef<ProductCatalogListItemResponseDto, any>[]>(
    () => [
      getCheckboxColumnDefs<ProductCatalogListItemResponseDto>(),
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <Anchor
            fz="xs"
            c="gray.9"
            td="underline"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            <TextCell value={info.getValue()} />
          </Anchor>
        ),
      }),
      columnHelper.accessor("purchaseOrderReference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) => (
          <TextCell
            value={info.row.original.purchaseOrderReference ?? undefined}
          />
        ),
      }),
      columnHelper.accessor("brandReference", {
        id: "reference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brand", {
        id: "brand",
        header: t("lists.columns.brand"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue() || "-"} />,
      }),
      columnHelper.accessor("color", {
        header: t("lists.columns.color"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brandType", {
        id: "type",
        header: t("lists.columns.type"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("collection", {
        header: t("lists.columns.collection"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.accessor("ingestedAt", {
        id: "ingestedAt",
        header: t("lists.columns.ingestedAt"),
        enableSorting: false,
        cell: (info) => <DateCell date={info.getValue()} format="DD/MM/YYYY" />,
      }),
      columnHelper.accessor("recycledMaterialPercentage", {
        header: t("lists.columns.recycledMaterialPercentage"),
        enableSorting: false,
        cell: (info) => (
          <TextCell
            value={
              info.getValue()?.cause !==
              recycledMaterialPercentageDtoCauseEnum["not-calculated"]
                ? info.getValue()?.percentage
                : "-"
            }
          />
        ),
      }),
      columnHelper.accessor("directSupplier", {
        id: "supplier",
        header: t("lists.columns.directSupplier"),
        enableSorting: false,
        cell: (info) => {
          const value = info.row.original.directSupplier
          return value?.email ? (
            <SupplierCell supplierEmail={value.email} />
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("lastPendingIrContact", {
        id: "lastPendingIrContact",
        header: t("lists.columns.pendingIrContact"),
        enableSorting: false,
        cell: (info) => {
          const lastPendingIrContact = info.getValue()
          return <LastPendingIrContactCell supplier={lastPendingIrContact} />
        },
      }),
      columnHelper.accessor("id", {
        id: "action-cell-details",
        header: undefined,
        enableSorting: false,
        enablePinning: false,
        cell: (info) => (
          <ActionIcon
            icon={<IconExternalLink />}
            onClick={() => navigate(`/products/details/${info.getValue()}`)}
          />
        ),
      }),
    ],
    [t, navigate]
  )
}
